import type { CuratorRosterRow } from '@/api/curatorGroups';

import { filterEnrollmentRows } from './enrollmentActions';

/**
 * Кто есть кто в списке учеников курса у куратора (D40, договор
 * `docs/CONTRACTS/curator-groups.md` §2 и §4).
 *
 * ⚠️ СЕРВЕРНОЕ `mine` СЧИТАЕТСЯ ТОЛЬКО ПО ГРУППЕ (`curator_groups.go`,
 * `leadsGroup`). Личный ученик самого куратора — тот, кого админ назначил ему по
 * D25, — приходит с `mine: false`. Доверять полю напрямую нельзя: куратор увидел
 * бы собственного ученика как «у другого» и пошёл бы забирать его у самого себя
 * через окно подтверждения.
 *
 * «Чужой» здесь — то же, что у сервера в §4: ученик в группе, где этого куратора
 * нет, либо без группы с ДРУГИМ личным куратором. Свободный, свой личный и
 * ученик своей группы подтверждения не требуют.
 */

export type Ownership = 'mine' | 'free' | 'other';

export function ownership(row: CuratorRosterRow, meId: string | undefined): Ownership {
  if (row.group_id) {
    // В группе решает только состав кураторов группы — это и есть серверное `mine`.
    if (row.mine) return 'mine';
    return meId && row.group_curators?.some((c) => c.id === meId) ? 'mine' : 'other';
  }
  if (!row.curator_id) return 'free';
  return meId && row.curator_id === meId ? 'mine' : 'other';
}

/** Что показать рядом с именем: группу, личного куратора или ничего. */
export type RowBadge = { kind: 'group'; name: string } | { kind: 'curator'; name: string } | { kind: 'none' };

export function rowBadge(row: CuratorRosterRow): RowBadge {
  // Имени может не прийти — пустые кавычки на экране хуже, чем ничего.
  if (row.group_id && row.group_name) return { kind: 'group', name: row.group_name };
  if (!row.group_id && row.curator_id && row.curator_name) return { kind: 'curator', name: row.curator_name };
  return { kind: 'none' };
}

export type RosterTab = 'all' | 'noGroup' | 'mine';

/**
 * Вкладка и поиск. Поиск — тот же, что в списке учеников марафона в админке
 * (`filterEnrollmentRows`): без учёта регистра, «ё» и «е» не различаются. Своей
 * копии не заводим, чтобы правила поиска не разъехались между экранами.
 */
export function filterRoster(
  rows: readonly CuratorRosterRow[],
  { tab, query, meId }: { tab: RosterTab; query: string; meId: string | undefined },
): CuratorRosterRow[] {
  const byTab = rows.filter((r) => {
    if (tab === 'noGroup') return !r.group_id;
    if (tab === 'mine') return ownership(r, meId) === 'mine';
    return true;
  });
  return filterEnrollmentRows(byTab, query);
}

/** Цифры на вкладках — считаются по тем же правилам, что и сами вкладки. */
export function tabCounts(rows: readonly CuratorRosterRow[], meId: string | undefined) {
  return {
    all: rows.length,
    noGroup: rows.filter((r) => !r.group_id).length,
    mine: rows.filter((r) => ownership(r, meId) === 'mine').length,
  };
}
